/* eslint-disable jsx-a11y/alt-text */
import { ModalContext } from '@/context/modalProvider'
import { animated, useTransition } from '@react-spring/web'
import classNames from 'classnames'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import { trackEvent } from '@/services/segment'

import { useShowDixa } from '@/hooks/useDixa'
import useWindowSize from '@/hooks/useWindowSize'

import Text, { TextProps } from '@/components/elements/atoms/Text'
import IconButton from '@/components/elements/molecules/IconButton/IconButton'
import SwiperSlider from '@/components/elements/molecules/SwiperSlider/SwiperSlider'
import { BREAKPOINTS } from '@/components/templates/Base'

import STYLES from './index.module.sass'

import { useDragBehavior } from '../hooks/useDragBehavior'

type ImageProp = React.ImgHTMLAttributes<HTMLImageElement>

type Props = {
  title: TextProps
  children: React.ReactNode
  images?: ImageProp[]
  padding?: boolean
  HeaderContent?: () => JSX.Element
  backgroundColor?: 'brand-yellow-100' | 'white'
}

const BASE_CONFIG = {
  desktop: {
    tension: 230,
    friction: 23,
    clamp: false
  },
  mobile: {
    tension: 240,
    friction: 18,
    clamp: false
  }
}

const MOBILE_ENTERING_ANIMATION_PADDING = {
  active: 'calc(12vh + 6.9rem)',
  offset: 'calc(-12vh)'
}

const mobileAnimations = {
  from: {
    top: '120%',
    transform: 'translate(-50%, 0%)'
  },
  to: {
    top: '100%',
    transform: 'translate(-50%, -100%)'
  },
  leave: {
    top: '100%',
    transform: 'translate(-50%, 0%)'
  },
  config: BASE_CONFIG.mobile
}

const desktopAnimations = {
  from: {
    top: '150%',
    transform: 'translate(-50%, -50%)'
  },
  to: {
    top: '50%',
    transform: 'translate(-50%, -50%)'
  },
  leave: {
    top: '150%',
    transform: 'translate(-50%, -50%)'
  },
  config: BASE_CONFIG.desktop
}

const overlayAnimations = {
  from: { opacity: 0 },
  enter: { opacity: 1 },
  leave: { opacity: 0 },
  config: { duration: 200 }
}

const Slider = ({
  images
}: {
  images: ImageProp[]
}): React.JSX.Element | null => {
  const { windowWidth } = useWindowSize()

  if (!images) return null

  if (images.length === 1) {
    const [i] = images

    const className = classNames(STYLES.slide, STYLES.singleSlide)

    return (
      <div className={className}>
        <img {...i} />
      </div>
    )
  }

  return (
    <SwiperSlider
      className={STYLES.slider}
      variant="brandYellow300"
      arrows={windowWidth >= BREAKPOINTS.md}
      slidesPerView={1}
      bullets={windowWidth < BREAKPOINTS.md}
      arrowsInside
      autoHeight
      paddingOverride={8}
      nextButtonClass={STYLES.nextButton}
      prevButtonClass={STYLES.prevButton}
    >
      {images.map((image) => (
        <div className={STYLES.slide} key={image.src}>
          <img {...image} />
        </div>
      ))}
    </SwiperSlider>
  )
}
/**
 * ModalLayout Component
 *
 * A modal layout component for displaying content in a modal dialog with animations,
 * adaptable to both mobile and desktop layouts. The modal uses `@react-spring/web`
 * for animated transitions and handles interactions such as closing via escape key
 * or clicking outside the modal.
 *
 * @param {TextProps} title - The title content displayed in the modal header.
 * @param {React.ReactNode} children - The children content rendered in the modal body.
 * @param {React.ReactNode} padding - @default false Adds left and right padding to modal
 * @param {string} images - Single or arra of images
 * @returns {JSX.Element} The rendered modal layout.
 */
const ModalLayout = ({
  children,
  title,
  images,
  padding = true,
  HeaderContent,
  backgroundColor = 'brand-yellow-100'
}: Props): React.JSX.Element => {
  useShowDixa(false)
  const { closeModal, id } = useContext(ModalContext)
  const { windowWidth } = useWindowSize()

  const ref = useRef<HTMLDivElement>(null)

  const [isVisible, setIsVisible] = useState(true)
  const [contentIndents, setContentIndents] = useState<{
    padding: string
    margin: string
  } | null>(null)

  const isMobile = windowWidth < BREAKPOINTS.md

  /**
   * Handles modal close action with animation
   */
  const handleClose = useCallback(
    (event?: React.MouseEvent | React.KeyboardEvent | React.TouchEvent) => {
      event?.preventDefault()
      event?.stopPropagation()

      trackEvent('Modal Closed', {
        component_identifier: id
      })

      // Start exit animation
      setIsVisible(false)

      // Delay actual closing to allow animation to complete
      setTimeout(() => {
        closeModal(id)
      }, 300)
    },
    [id, closeModal]
  )

  const handleTabOut = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key !== 'Tab') return
      const target = event.target as HTMLElement
      const isModal = target.id === id && target === document.activeElement
      const isCloseButton = document
        .getElementById('modal-close-button')
        ?.contains(target)

      if (!isModal && !isCloseButton) return

      if (event.shiftKey !== isCloseButton) {
        handleClose(event)
      }
    },
    [handleClose, id]
  )

  /**
   * Mobile drawer drag behavior
   *
   * Enables native-feeling interactions:
   * - Stretches with scaleY when pulled up
   * - Follows finger when pulled down
   * - Auto-closes when pulled down past threshold
   */
  const {
    dragStyles,
    onTouchStart: dragTouchStart,
    onTouchMove: dragTouchMove,
    onTouchEnd: dragTouchEnd
  } = useDragBehavior({
    handleOnClose: handleClose
  })

  // Overlay animation
  const overlayTransition = useTransition(isVisible, overlayAnimations)

  // Content animation
  const contentTransition = useTransition(isVisible, {
    from: isMobile ? mobileAnimations.from : desktopAnimations.from,
    enter: isMobile ? mobileAnimations.to : desktopAnimations.to,
    leave: isMobile ? mobileAnimations.leave : desktopAnimations.leave,
    config: isMobile ? mobileAnimations.config : desktopAnimations.config,
    /**
     * Apply mobile-specific padding during entrance animation.
     * This is needed to avoid gap between content and bottom of the screen
     */
    onStart: () => {
      if (isVisible && isMobile) {
        setContentIndents({
          padding: MOBILE_ENTERING_ANIMATION_PADDING.active,
          margin: MOBILE_ENTERING_ANIMATION_PADDING.offset
        })
      }
    },
    // Reset applied padding after animation completes
    onRest: () => {
      if (isVisible && isMobile) {
        setContentIndents(null)
      }
    }
  })

  useEffect(() => {
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = 'auto'
    }
  }, [])

  useEffect(() => {
    /**
     * Handles closing the modal when the Escape key is pressed.
     */
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') handleClose()
    }

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const isSame = ref.current?.isEqualNode(event.target as Node)

      if (isSame) return

      if (!ref?.current?.contains(event.target as Node)) {
        setTimeout(() => {
          handleClose()
        }, 0)
      }
    }

    window.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('touchstart', handleClickOutside)
    window.addEventListener('keyup', handleEsc)

    return () => {
      window.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('touchstart', handleClickOutside)
      window.removeEventListener('keyup', handleEsc)
    }
  }, [ref, closeModal, id, handleClose])

  useEffect(() => {
    const modal = ref.current

    // Set focus to the modal when it is displayed
    modal?.focus()

    return () => {
      document.getElementById('trigger-' + id)?.focus()
    }
  }, [ref, handleClose, closeModal, id])

  const zIndex = useMemo(() => {
    const intercom = document.querySelector('.intercom-launcher')

    if (intercom) {
      const intercomZIndex = window.getComputedStyle(intercom).zIndex
      return parseInt(intercomZIndex, 10) + 1
    }
    return 1000
  }, [])

  const padderClass = classNames({
    [STYLES.padder]: padding
  })

  const contentClass = classNames(STYLES.content, [STYLES[backgroundColor]])

  return (
    <>
      {overlayTransition(
        (overlayStyle, overlayItem) =>
          overlayItem && (
            <animated.div
              style={{ ...overlayStyle, zIndex }}
              className={STYLES.overlayContainer}
            >
              <div className={STYLES.overlay}>
                {contentTransition(
                  (contentStyle, contentItem) =>
                    contentItem && (
                      <animated.div
                        className={STYLES.wrapper}
                        style={contentStyle}
                      >
                        <animated.div
                          role="dialog"
                          aria-modal="true"
                          aria-labelledby="modal-title"
                          tabIndex={0} // Make the modal focusable
                          className={contentClass}
                          ref={ref}
                          id={id}
                          onKeyDown={handleTabOut}
                          style={{
                            ...(contentIndents
                              ? {
                                  paddingBottom: contentIndents.padding,
                                  marginBottom: contentIndents.margin
                                }
                              : {}),
                            ...(isMobile ? dragStyles : {})
                          }}
                        >
                          <div
                            className={STYLES.mobileHeaderFlex}
                            onTouchStart={dragTouchStart}
                            onTouchMove={dragTouchMove}
                            onTouchEnd={dragTouchEnd}
                          >
                            <div className={STYLES.mobileBar} />
                          </div>

                          <div id="modal-title" className={STYLES.title}>
                            <div className={STYLES.padder}>
                              <Text
                                variant="display28"
                                margin={false}
                                {...title}
                              />
                              {HeaderContent && <HeaderContent />}
                            </div>
                          </div>

                          <div className={STYLES.children}>
                            {images && <Slider images={images} />}

                            <div className={padderClass}>{children}</div>
                          </div>

                          <div
                            id="modal-close-button"
                            className={STYLES.closeButton}
                            onKeyDown={handleTabOut}
                            role="none"
                          >
                            <IconButton
                              className={STYLES.closeButtonIcon}
                              variant={
                                backgroundColor === 'white'
                                  ? 'brandBlue100'
                                  : 'brandYellow300'
                              }
                              iconSize={0.4}
                              size={40}
                              icon="closeBold"
                              shape="circle"
                              onClick={handleClose}
                            />
                          </div>
                        </animated.div>
                      </animated.div>
                    )
                )}
              </div>
            </animated.div>
          )
      )}
    </>
  )
}

export default ModalLayout
